// Download Brochure modal. Opens from any element carrying
// `data-db-brochure-open` (e.g. the action card in bm-quick-actions).
//
// On submit: validates, runs a Cloudflare Turnstile challenge, POSTs the
// fields to the form-handling Worker (`settings.forms_worker_url`) as
// `form_type=brochure_download`, and on a confirmed `{status:"ok"}`
// response reveals the "check your inbox" success state + pushes a
// `brochure_download` event to dataLayer. The Worker forwards the lead to
// Klaviyo (Klaviyo-only — no notification email); a Klaviyo flow emails
// the brochure PDF using the `brochure_url` event property.
//
// The brochure URL + brand are resolved per-trigger from
// `data-db-brochure-url` / `data-db-brochure-brand`, falling back to the
// modal's `data-brochure-url` setting, and stashed in hidden inputs.
//
// Self-contained (does NOT use the `data-tb-worker-form` path in
// tb-forms.js) because the modal owns its own success-panel UX, focus
// trap, and reset-on-reopen lifecycle. The Turnstile API loader is shared
// (snippets/tb-forms-captcha.liquid); only the per-widget mount is local.

(function () {
  if (customElements.get('db-brochure-modal')) return;

  const tb = (window.taylorsBoats = window.taylorsBoats || {});

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  class DbBrochureModal extends HTMLElement {
    constructor() {
      super();
      /** @type {HTMLFormElement | null} */
      this._form = null;
      /** @type {HTMLElement | null} */
      this._success = null;
      /** @type {HTMLElement | null} */
      this._lastFocus = null;
      /** @type {string | number | null} */
      this._widgetId = null;
      /** @type {HTMLButtonElement | null} */
      this._submitBtn = null;
      this._submitLabel = '';
      this._pending = false;
      this._onKeydown = this._onKeydown.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
    }

    connectedCallback() {
      const form = this.querySelector('[data-db-form]');
      this._form = form instanceof HTMLFormElement ? form : null;
      const success = this.querySelector('[data-db-success]');
      this._success = success instanceof HTMLElement ? success : null;
      const submit = this._form && this._form.querySelector('button[type="submit"]');
      this._submitBtn = submit instanceof HTMLButtonElement ? submit : null;
      this._submitLabel = this._submitBtn ? this._submitBtn.textContent : '';

      this.addEventListener('click', this._onClick);
      if (this._form) this._form.addEventListener('submit', this._onSubmit);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      if (this._form) this._form.removeEventListener('submit', this._onSubmit);
      document.removeEventListener('keydown', this._onKeydown);
    }

    /** @param {Element | null} trigger */
    open(trigger) {
      this._lastFocus = trigger instanceof HTMLElement ? trigger : null;

      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKeydown);

      // Reset first — form.reset() clears field values, so the hidden
      // inputs below must be set AFTER it or they'd be wiped.
      this._resetToForm();

      const overrideUrl = trigger && trigger.getAttribute('data-db-brochure-url');
      const url = overrideUrl || this.getAttribute('data-brochure-url') || '';
      this._setHiddenInput('contact[brochure_url]', url);

      const brand = (trigger && trigger.getAttribute('data-db-brochure-brand')) || '';
      this._setHiddenInput('brand', brand);

      this._mountTurnstile();

      const firstField = this.querySelector('input, select, textarea, button');
      if (firstField instanceof HTMLElement) firstField.focus();
    }

    /**
     * @param {string} name
     * @param {string} value
     */
    _setHiddenInput(name, value) {
      if (!this._form) return;
      let input = this._form.querySelector(`input[name="${name}"]`);
      if (!(input instanceof HTMLInputElement)) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        this._form.appendChild(input);
      }
      input.value = value;
    }

    _mountTurnstile() {
      if (!this._form || this._widgetId != null) return;
      if (!tb.turnstileSiteKey) return;
      if (typeof turnstile === 'undefined' || !turnstile.render) return;
      let mount = this._form.querySelector('[data-db-turnstile]');
      if (!mount) {
        mount = document.createElement('div');
        mount.setAttribute('data-db-turnstile', '');
        this._form.appendChild(mount);
      }
      try {
        this._widgetId = turnstile.render(mount, {
          sitekey: tb.turnstileSiteKey,
          appearance: 'interaction-only',
          action: 'brochure_download',
        });
      } catch (e) {
        console.error('[db-brochure] Turnstile mount failed:', e);
      }
    }

    /** @returns {Promise<string|null>} */
    _getTurnstileToken() {
      return new Promise((resolve) => {
        if (this._widgetId == null || typeof turnstile === 'undefined' || !turnstile.execute) {
          resolve(null);
          return;
        }
        const existing = turnstile.getResponse(this._widgetId);
        if (existing) {
          resolve(existing);
          return;
        }
        try {
          turnstile.execute(this._widgetId, {
            callback: (token) => resolve(token),
            'error-callback': () => resolve(null),
          });
        } catch (e) {
          resolve(null);
        }
      });
    }

    _resetTurnstile() {
      if (typeof turnstile !== 'undefined' && turnstile.reset && this._widgetId != null) {
        try {
          turnstile.reset(this._widgetId);
        } catch (e) {
          /* ignore */
        }
      }
    }

    _pushDataLayer() {
      window.dataLayer = window.dataLayer || [];
      const brandInput = this._form && this._form.querySelector('input[name="brand"]');
      window.dataLayer.push({
        event: 'brochure_download',
        form_type: 'brochure_download',
        brand: brandInput instanceof HTMLInputElement ? brandInput.value : '',
      });
    }

    /** @param {boolean} isSubmitting */
    _setSubmitting(isSubmitting) {
      if (!this._submitBtn) return;
      this._submitBtn.disabled = isSubmitting;
      this._submitBtn.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
      this._submitBtn.textContent = isSubmitting ? 'Sending…' : this._submitLabel;
    }

    /** @param {string} message */
    _showError(message) {
      const el = this.querySelector('[data-db-error]');
      if (el instanceof HTMLElement) {
        el.textContent = message;
        el.hidden = false;
      }
    }

    _clearError() {
      const el = this.querySelector('[data-db-error]');
      if (el instanceof HTMLElement) {
        el.textContent = '';
        el.hidden = true;
      }
    }

    close() {
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKeydown);
      if (this._lastFocus) this._lastFocus.focus();
    }

    _resetToForm() {
      if (!this._form || !this._success) return;
      this._form.hidden = false;
      this._success.hidden = true;
      this._form.reset();
      this._clearError();
    }

    _showSuccess() {
      if (!this._form || !this._success) return;
      this._form.hidden = true;
      this._success.hidden = false;
      this._success.setAttribute('tabindex', '-1');
      this._success.focus();
    }

    /** @param {Event} event */
    _onClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-db-close]')) {
        event.preventDefault();
        this.close();
      }
    }

    /** @param {KeyboardEvent} event */
    _onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'Tab') {
        this._trapFocus(event);
      }
    }

    /** @param {KeyboardEvent} event */
    _trapFocus(event) {
      /** @type {HTMLElement[]} */
      const focusables = [];
      this.querySelectorAll(FOCUSABLE).forEach((el) => {
        if (el instanceof HTMLElement && !el.hidden && el.offsetParent !== null) {
          focusables.push(el);
        }
      });
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /** @param {SubmitEvent} event */
    async _onSubmit(event) {
      event.preventDefault();
      if (!this._form || this._pending) return;
      // reportValidity() shows the native bubble and moves focus, but does not
      // set aria-invalid. Flag each control so screen readers can identify which
      // fields are in error (WCAG 3.3.1); cleared on the next valid pass.
      this._form.querySelectorAll('input, select, textarea').forEach((field) => {
        if (!field.willValidate) return;
        if (field.checkValidity()) {
          field.removeAttribute('aria-invalid');
        } else {
          field.setAttribute('aria-invalid', 'true');
        }
      });
      if (!this._form.checkValidity()) {
        this._form.reportValidity();
        return;
      }

      // No Worker URL configured (dev / pre-launch): show the success
      // panel so the modal still demos, but nothing is captured.
      if (!tb.formsWorkerUrl) {
        this._showSuccess();
        return;
      }

      this._pending = true;
      this._clearError();
      this._setSubmitting(true);

      const token = await this._getTurnstileToken();
      if (token != null) this._setHiddenInput('cf-turnstile-response', token);

      let res, data;
      try {
        res = await fetch(tb.formsWorkerUrl, { method: 'POST', body: new FormData(this._form) });
      } catch (e) {
        this._pending = false;
        this._setSubmitting(false);
        this._showError('Network error. Please check your connection and try again.');
        this._resetTurnstile();
        return;
      }
      try {
        data = await res.json();
      } catch (e) {
        data = {};
      }
      this._pending = false;
      this._setSubmitting(false);

      if (res.ok && data.status === 'ok') {
        if (data.form_type) this._pushDataLayer();
        this._showSuccess();
      } else {
        this._showError((data && data.error) || 'Something went wrong. Please try again.');
        this._resetTurnstile();
      }
    }
  }

  customElements.define('db-brochure-modal', DbBrochureModal);

  /**
   * @param {Element} trigger
   * @returns {DbBrochureModal | null}
   */
  function findModal(trigger) {
    const id = trigger.getAttribute('data-db-brochure-target');
    if (id) {
      const byId = document.getElementById(id);
      if (byId instanceof DbBrochureModal) return byId;
    }
    const fallback = document.querySelector('db-brochure-modal');
    return fallback instanceof DbBrochureModal ? fallback : null;
  }

  document.addEventListener('click', function (event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('[data-db-brochure-open]');
    if (!trigger) return;
    const modal = findModal(trigger);
    if (!modal) return;
    event.preventDefault();
    modal.open(trigger);
  });
})();
